import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TableColumn } from '@/components/table';
import { useGraphUser } from '../../hooks/graph/use-graph-user';
import type { LabelData, PrinterData, PrintJobData } from '../../types/types';
import { Table } from '../table';

// ── Helpers ───────────────────────────────────────────────────────────────────

const CreatedByDisplay = ({ oid }: { oid: string }) => {
  const { data } = useGraphUser(oid);
  return <span className="text-core-slate-1300">{data?.displayName ?? oid}</span>;
};

// ── Types ─────────────────────────────────────────────────────────────────────

type PrintJobTableRow = PrintJobData & { displayId?: number };

type Props = {
  jobs: PrintJobData[];
  printerMap: Map<number, PrinterData>;
  labelMap?: Map<number, LabelData>;
  /** When true, shows an ID column using displayId instead of the raw DB id. */
  showId?: boolean;
  /** When true, shows the Label column (not needed when already scoped to one form/label). */
  showLabel?: boolean;
  /**
   * Disable the in-table search box. Use when the parent loads jobs via
   * infinite scroll so client-side filtering won't silently hide matches that
   * live in not-yet-loaded pages.
   */
  searchable?: boolean;
  /**
   * Disable column sort headers. Use alongside `searchable={false}` when the
   * parent loads jobs via infinite scroll — client-side sort would reorder
   * only the loaded subset and mislead the user about global order.
   */
  sortable?: boolean;
  totalCount?: number;
  onRowClick?: (job: PrintJobTableRow) => void;
  ariaLabel?: string;
};

// ── Component ─────────────────────────────────────────────────────────────────

export const PrintJobsTable = ({
  jobs,
  printerMap,
  labelMap,
  showId = false,
  showLabel = false,
  searchable = true,
  sortable = true,
  totalCount,
  onRowClick,
  ariaLabel,
}: Props) => {
  const { t } = useTranslation();

  const tableData: PrintJobTableRow[] = useMemo(
    () => jobs.map((item, index) => ({ ...item, displayId: index + 1 })),
    [jobs]
  );

  const columns = useMemo(() => {
    const cols: TableColumn<PrintJobTableRow>[] = [];

    if (showId) {
      cols.push({
        key: 'displayId',
        label: 'ID',
        sortable: true,
        render: (value) => <span className="text-core-slate-1300 font-medium">#{value}</span>,
      });
    }

    cols.push({
      key: 'status',
      label: t('printJobsPage.display.status'),
      sortable: true,
      render: (value) => (
        <span className={value as string}>
          {(value as string).charAt(0).toUpperCase() + (value as string).slice(1)}
        </span>
      ),
    });

    cols.push({
      key: 'printerId',
      label: t('printJobsPage.display.printer'),
      sortable: true,
      accessor: (job: PrintJobData) =>
        printerMap.get(job.printerId)?.name ?? `ID: ${job.printerId}`,
    });

    if (showLabel && labelMap) {
      cols.push({
        key: 'labelId',
        label: t('printJobsPage.display.label'),
        sortable: true,
        accessor: (job: PrintJobData) => labelMap.get(job.labelId)?.name ?? `ID: ${job.labelId}`,
      });
    }

    cols.push(
      {
        key: 'labelRevision',
        label: t('printJobsPage.display.labelRevision'),
        sortable: true,
        render: (value) => <span className="text-core-slate-1300">{(value as string) || '-'}</span>,
      },
      {
        key: 'quantity',
        label: t('printJobsPage.display.quantity'),
        sortable: true,
        render: (value) => <span className="text-core-slate-1300">{value}</span>,
      },
      {
        key: 'createdBy',
        label: t('printJobsPage.display.createdBy'),
        sortable: true,
        render: (value) => <CreatedByDisplay oid={value as string} />,
      },
      {
        key: 'createdAt',
        label: t('printJobsPage.display.createdAt'),
        sortable: true,
        accessor: (job: PrintJobData) =>
          new Date(job.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }),
      }
    );

    return cols.map((col) => ({ ...col, sortable: sortable && col.sortable }));
  }, [printerMap, labelMap, showId, showLabel, sortable, t]);

  return (
    <Table
      data={tableData}
      columns={columns as TableColumn<Record<string, unknown>>[]}
      emptyMessage={t('printJobsPage.display.noPrintJobsFound')}
      searchable={searchable}
      searchPlaceholder={t('printJobsPage.display.searchPrintJobsPlaceholder')}
      totalCount={totalCount}
      onRowClick={onRowClick as ((row: Record<string, unknown>) => void) | undefined}
      ariaLabel={ariaLabel}
    />
  );
};
